import { PropsWithChildren, ReactNode, SVGProps } from "react"
import { CanvasControlsContext } from "@/components/canvas-controls/CanvasControlsContext"
import {
	getSVGTransform,
	getViewportRect,
} from "@/components/canvas-controls/canvas-controls-utils"
import { useObserveEditorViewport } from "@/utils/useObserveEditorViewport"
import { useEditor } from "@/utils/useEditor"
import clsx from "clsx"

const DEBUG = false

interface ScreenSpaceViewport {
	width: number
	height: number
}

interface Props extends PropsWithChildren<SVGProps<SVGSVGElement>> {
	hidden?: boolean
	screenSpaceChildren?: ReactNode | ((viewport: ScreenSpaceViewport) => ReactNode)
}

export const CanvasControlsOverlay = ({
	children,
	className,
	hidden,
	screenSpaceChildren,
	style,
	...props
}: Props) => {
	useObserveEditorViewport()

	const editor = useEditor()
	const viewportRect = editor.mainViewport.viewportRect
	const viewportPixelRect = getViewportRect()
	const { transform, imageWidth, imageHeight } = getSVGTransform()
	const fixedChildren =
		typeof screenSpaceChildren === "function"
			? screenSpaceChildren({
					width: viewportPixelRect.width,
					height: viewportPixelRect.height,
				})
			: screenSpaceChildren

	return (
		<CanvasControlsContext.Provider
			value={{
				parentX: 0,
				parentY: 0,
				parentWidth: imageWidth,
				parentHeight: imageHeight,
			}}
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				className={clsx("absolute transition-opacity", className)}
				style={{
					left: `${viewportRect[0] * 100}%`,
					top: `${viewportRect[1] * 100}%`,
					width: `${viewportRect[2] * 100}%`,
					height: `${viewportRect[3] * 100}%`,
					opacity: hidden ? 0 : 1,
					pointerEvents: hidden ? "none" : "unset",
					zIndex: 1,
					...style,
				}}
				fill="transparent"
				preserveAspectRatio="none"
				{...props}
			>
				{DEBUG && (
					<g transform={transform}>
						<rect
							x={0}
							y={0}
							width={imageWidth}
							height={imageHeight}
							strokeWidth={1}
							stroke={"red"}
						/>
					</g>
				)}
				<g transform={transform}>{children}</g>
				{fixedChildren}
			</svg>
		</CanvasControlsContext.Provider>
	)
}
